/**
 * Tenant white-label branding — applies saved branding (colors, app name,
 * logo, favicon) to the live app. Colors are converted to the HSL-triplet
 * format the Tailwind theme tokens expect (`hsl(var(--primary))`).
 *
 * Branding stores a light set (primary_color, accent_color, sidebar_bg,
 * logo_url) plus optional dark overrides (*_dark). The correct set is
 * chosen from the active theme — `dark` (or any dark-kind theme) adds the
 * `dark` class to <html>, so we key off that. Any blank dark value falls
 * back to its light counterpart.
 */
import { api } from '@/lib/api';
import { useState, useEffect } from 'react';

function hexToHslTriplet(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function isDark() {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

// Point the PWA <link rel="manifest"> at the dynamic per-tenant manifest so
// Chrome's "Install app" prompt shows the tenant's name + logo icon (when
// branding is populated); otherwise the endpoint returns the INZARA defaults.
// Also updates the iOS apple-touch-icon to the brand icon when present.
function applyManifestFromBranding(r) {
  if (typeof document === 'undefined') return;
  try {
    const origin = window.location.origin;
    const backend = process.env.REACT_APP_BACKEND_URL || origin;
    // Only http(s) or root-relative asset paths can be manifest icon URLs — a
    // base64 data-URI logo can't be passed on the query string, so skip it and
    // fall through to the next candidate (favicon) or the default icons.
    const pickIcon = (u) => {
      if (!u) return '';
      if (/^https?:\/\//i.test(u)) return u;
      if (u.startsWith('/')) return origin + u;
      return '';
    };
    const icon = pickIcon(r.app_icon_url) || pickIcon(r.logo_url) || pickIcon(r.favicon_url) || '';
    const params = new URLSearchParams();
    if (r.app_name) params.set('name', r.app_name);
    if (r.primary_color) params.set('theme', r.primary_color);
    if (icon) params.set('icon', icon);
    params.set('origin', origin);
    const href = `${backend}/api/public/manifest.webmanifest?${params.toString()}`;
    let link = document.querySelector("link[rel='manifest']");
    if (!link) { link = document.createElement('link'); link.rel = 'manifest'; document.head.appendChild(link); }
    if (link.href !== href) link.href = href;
    if (icon) {
      let apple = document.querySelector("link[rel='apple-touch-icon']");
      if (!apple) { apple = document.createElement('link'); apple.rel = 'apple-touch-icon'; document.head.appendChild(apple); }
      apple.href = icon;
    }
  } catch (_) { /* manifest update best-effort */ }
}

// Resolve the mode-appropriate values, falling back to the light set.
export function resolveBranding(b, dark) {
  const src = b || {};
  const pick = (base) => (dark && src[`${base}_dark`]) || src[base] || '';
  return {
    app_name: src.app_name || '',
    favicon_url: src.favicon_url || '',
    app_icon_url: src.app_icon_url || '',
    logo_url: pick('logo_url'),
    primary_color: pick('primary_color'),
    accent_color: pick('accent_color'),
    sidebar_bg: pick('sidebar_bg'),
    login_slug: src.login_slug || '',
  };
}

// Persisted so the correct tenant branding can be applied synchronously on the
// very next page load / route (before the async /branding fetch resolves),
// which eliminates the INZARA→tenant "flash" on login and logout.
const LS_KEY = 'inzara.branding';
function readCachedBranding() {
  try {
    const s = window.localStorage.getItem(LS_KEY);
    if (s) return JSON.parse(s);
  } catch (_) { /* storage disabled / bad JSON */ }
  return (typeof window !== 'undefined' && window.__INZARA_BRANDING__) || {};
}
let _current = (typeof window !== 'undefined' && readCachedBranding()) || {};

// ── White-label terminology ────────────────────────────────────────────
// Each renamable area ships with a default plural (the area label) and, where
// it makes sense, a singular (one item). Admins override these in Branding.
// A blank override falls back to the default so labels are never empty.
export const TERM_AREAS = [
  { key: 'home', plural: 'Home', singular: '' },
  { key: 'quickstart', plural: 'Quick Start', singular: '' },
  { key: 'insights', plural: 'Dashboards & Reports', singular: 'Dashboard/Report' },
  { key: 'ai_explore', plural: 'Ask AI', singular: 'Ask AI' },
  { key: 'ai_insights', plural: 'Findings', singular: 'Finding' },
  { key: 'investigations', plural: 'Investigations', singular: 'Investigation' },
  { key: 'discussions', plural: 'Discussions', singular: 'Discussion' },
  { key: 'visual_library', plural: 'Visual Library', singular: 'Visual' },
  { key: 'kpi_monitor', plural: 'KPI Monitor', singular: 'KPI' },
  { key: 'apps', plural: 'Apps', singular: 'App' },
  { key: 'marketplace', plural: 'Marketplace', singular: '' },
  { key: 'data', plural: 'Data', singular: 'Dataset' },
  { key: 'sources', plural: 'Sources', singular: 'Source' },
  { key: 'sentinels', plural: 'Anomaly Scans', singular: 'Anomaly Scan' },
  { key: 'forecast_sentinels', plural: 'Sentinels', singular: 'Sentinel' },
];
const TERM_DEFAULTS = Object.fromEntries(TERM_AREAS.map((a) => [a.key, { plural: a.plural, singular: a.singular }]));

// Resolve a configured term. form: 'plural' (default) | 'singular'.
// case: 'title' (default) | 'lower' (mid-sentence lowercase, derived).
export function getTerm(key, form = 'plural', textCase = 'title') {
  const cfg = (_current && _current.terms && _current.terms[key]) || {};
  const def = TERM_DEFAULTS[key] || {};
  let val = (cfg[form] || '').trim() || def[form] || def.plural || '';
  if (textCase === 'lower' && val) {
    // Lowercase unless the word looks like an acronym/brand (KPI, AI) — keep
    // all-caps tokens as-is so "AI Explore" doesn't become "ai explore".
    val = val.split(' ').map((w) => (w.length > 1 && w === w.toUpperCase() ? w : w.toLowerCase())).join(' ');
  }
  return val;
}

// Reactive terminology accessor — re-renders the consumer on branding change.
export function useTerms() {
  const [, force] = useState(0);
  useEffect(() => {
    const upd = () => force((n) => n + 1);
    window.addEventListener('branding:updated', upd);
    return () => window.removeEventListener('branding:updated', upd);
  }, []);
  return getTerm;
}


export function getBranding() { return resolveBranding(_current, isDark()); }
export function getBrandName() { return getBranding().app_name || 'INZARA'; }
export function getRawBranding() { return _current; }

// Derive developer-facing SDK identifiers from the brand name so white-label
// tenants never see "INZARA" in embed snippets / package names. Uses the first
// word of the app name, alnum-only. Defaults keep the original INZARA naming.
function brandSlugOf(name) {
  const first = String(name || '').trim().split(/\s+/)[0] || '';
  return first.toLowerCase().replace(/[^a-z0-9]/g, '') || 'inzara';
}
function embedComponentOf(slug) { return slug.charAt(0).toUpperCase() + slug.slice(1) + 'Embed'; }
export function getBrandSlug() { return brandSlugOf(getBrandName()); }
export function getBrandEmbedComponent() { return embedComponentOf(getBrandSlug()); }
export function getBrandPackageScope() { return `@${getBrandSlug()}`; }

export function applyBrandingToDOM(b, darkOverride) {
  if (typeof document === 'undefined') return;
  if (b) _current = b;
  const root = document.documentElement;
  const dark = typeof darkOverride === 'boolean' ? darkOverride : isDark();
  const r = resolveBranding(_current, dark);
  const p = hexToHslTriplet(r.primary_color); if (p) root.style.setProperty('--primary', p);
  const a = hexToHslTriplet(r.accent_color); if (a) root.style.setProperty('--accent', a);
  if (r.sidebar_bg) root.style.setProperty('--brand-sidebar-bg', r.sidebar_bg);
  else root.style.removeProperty('--brand-sidebar-bg');
  // Never override the shared --glass-bg (used by popups/dropdowns/dialogs).
  root.style.removeProperty('--glass-bg');
  if (r.app_name) document.title = r.app_name;
  if (r.favicon_url) {
    let link = document.querySelector("link[rel='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = r.favicon_url;
  }
  applyManifestFromBranding(r);
  window.__INZARA_BRANDING__ = _current;
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(_current)); } catch (_) { /* storage disabled */ }
  window.dispatchEvent(new CustomEvent('branding:updated', { detail: r }));
}

// Apply cached branding to the DOM immediately on module load so colors,
// title, favicon and logo are already correct before React renders — no
// INZARA default flash while the fresh /branding fetch is in flight.
if (typeof document !== 'undefined' && _current && Object.keys(_current).length) {
  applyBrandingToDOM(_current);
}

// Re-apply the current branding for the active theme (call on theme switch).
// Pass an explicit `dark` flag on theme toggle — the DOM class may not be
// applied yet when this runs, so relying on isDark() would use the stale mode.
export function reapplyBranding(darkOverride) { applyBrandingToDOM(null, darkOverride); }

export async function loadAndApplyBranding() {
  try {
    const b = await api.getBranding();
    applyBrandingToDOM(b || {});
    return b;
  } catch (e) {
    return null;
  }
}

// Reactive branded app name — re-renders the consuming component when
// branding loads/changes (via the 'branding:updated' event).
export function useBrandName() {
  const [name, setName] = useState(getBranding().app_name || 'INZARA');
  useEffect(() => {
    const upd = () => setName(getBranding().app_name || 'INZARA');
    upd();
    window.addEventListener('branding:updated', upd);
    return () => window.removeEventListener('branding:updated', upd);
  }, []);
  return name;
}

// Reactive brand bundle for developer/embed surfaces: { name, slug,
// embedComponent, packageScope }. Re-renders on 'branding:updated'.
export function useBrand() {
  const compute = () => {
    const name = getBranding().app_name || 'INZARA';
    const slug = brandSlugOf(name);
    return { name, slug, embedComponent: embedComponentOf(slug), packageScope: `@${slug}` };
  };
  const [b, setB] = useState(compute);
  useEffect(() => {
    const upd = () => setB(compute());
    upd();
    window.addEventListener('branding:updated', upd);
    return () => window.removeEventListener('branding:updated', upd);
  }, []);
  return b;
}
